import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { OnboardingBanner } from "@/components/OnboardingBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  Wallet,
  Search,
  Share2,
  Clock3,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useFirstTransaction } from "@/hooks/useFirstTransaction";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_COLORS = [
  "#3d8b3d", "#2196F3", "#FF9800", "#E91E63", "#9C27B0",
  "#00BCD4", "#FF5722", "#607D8B", "#795548", "#4CAF50",
];

export default function Dashboard() {
  const { t, formatCurrency, formatDate, categoryName, ui } = useI18n();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [transactionType, setTransactionType] = useState("all");
  const [transactionCategory, setTransactionCategory] = useState("all");
  const navigate = useNavigate();
  const { hasFirstTransaction } = useFirstTransaction();
  const { data: summary, isLoading: summaryLoading } = trpc.transactions.monthSummary.useQuery({
    month, year,
  });
  const { data: monthlyTotals = [], isLoading: totalsLoading } = trpc.transactions.monthlyTotals.useQuery({ months: 6 });
  const { data: categorySpending = [] } = trpc.transactions.categorySpending.useQuery({ month, year });
  const { data: categories = [] } = trpc.categories.list.useQuery({});
  // Memoize date inputs to prevent new Date() on every render causing infinite refetch
  const dateFrom = useMemo(() => new Date(year, month - 1, 1), [year, month]);
  const dateTo = useMemo(() => new Date(year, month, 0, 23, 59, 59), [year, month]);
  const { data: billsDueSoon = [] } = trpc.bills.getDueSoon.useQuery({ days: 7 });
  const { data: accessStatus } = trpc.admin.getMyAccessStatus.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: recentTx = [], isLoading: txLoading } = trpc.transactions.list.useQuery({
    limit: 100,
    dateFrom,
    dateTo,
  });

  const categoryLabel = (categoryId?: number | null) => {
    const category = categories.find((item) => item.id === categoryId);
    if (!category) return "";
    const parent = category.parentId ? categories.find((item) => item.id === category.parentId) : null;
    return parent ? `${categoryName(parent.name)} / ${categoryName(category.name)}` : categoryName(category.name);
  };

  const filteredRecentTx = recentTx.filter((tx) => {
    const label = categoryLabel(tx.categoryId);
    const searchText = `${tx.description ?? ""} ${label}`.toLocaleLowerCase();
    return (!transactionSearch || searchText.includes(transactionSearch.toLocaleLowerCase()))
      && (transactionType === "all" || tx.type === transactionType)
      && (transactionCategory === "all" || String(tx.categoryId) === transactionCategory);
  });

  const income = summary?.income ?? 0;
  const expense = summary?.expense ?? 0;
  const balance = income - expense;

  // Build cumulative balance for line chart
  const lineData = monthlyTotals.map((m, i) => {
    const cumBalance = monthlyTotals.slice(0, i + 1).reduce((acc, cur) => acc + cur.income - cur.expense, 0);
    return { ...m, balance: cumBalance };
  });

  // Category pie data
  const pieData = categorySpending
    .filter((s) => s.total > 0)
    .map((s) => {
      const cat = categories.find((c) => c.id === s.categoryId);
      return { name: categoryName(cat?.name) || categoryName("Outros"), value: s.total };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const monthLabel = formatDate(new Date(year, month - 1, 1), { month: "long", year: "numeric" });

  const trialDueAt = accessStatus?.isTrial ? accessStatus.nextBillingDueAt : null;
  const trialDays = trialDueAt ? Math.ceil((trialDueAt - Date.now()) / (24 * 60 * 60 * 1000)) : null;
  const shareViaWhatsApp = () => {
    const message = `${t.dashboard.inviteTitle}\n\n${t.dashboard.inviteDescription}\n\nhttps://bolso.tec.br`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <BolsoLayout onNewTransaction={() => setShowForm(true)}>
      <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6">
        {/* Recursos secundários são liberados somente após o primeiro lançamento. */}
        {!hasFirstTransaction && <OnboardingBanner onStartFirstTransaction={() => setShowForm(true)} />}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-serif font-bold text-foreground">{t.dashboard.title}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground capitalize">{monthLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {accessStatus?.isTrial && trialDays !== null && (
            <Card className={cn(
              "border shadow-sm",
              trialDays <= 5 ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30" : "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30"
            )} role="status" aria-live="polite">
              <CardContent className="flex items-center gap-3 p-4">
                <Clock3 className={cn("h-5 w-5 shrink-0", trialDays <= 5 ? "text-amber-700" : "text-emerald-700")} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {trialDays > 1 ? t.dashboard.trialDays.replace("{days}", String(trialDays)) : trialDays === 1 ? t.dashboard.trialToday : t.dashboard.trialEnded}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatDate(new Date(trialDueAt as number), { day: "2-digit", month: "2-digit", year: "numeric" })}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-primary/20 bg-primary/5 shadow-sm">
            <CardContent className="flex items-center gap-3 p-4">
              <Share2 className="h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{t.dashboard.inviteTitle}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.dashboard.inviteDescription}</p>
              </div>
              <Button size="sm" onClick={shareViaWhatsApp} className="shrink-0 gap-2">
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">{t.dashboard.inviteButton}</span>
                <span className="sm:hidden">WhatsApp</span>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {summaryLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}><CardContent className="p-4"><Skeleton className="h-16" /></CardContent></Card>
            ))
          ) : (
            <>
              <Card className="border-border shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t.dashboard.monthBalance}</p>
                      <p className={cn("text-xl font-bold mt-1 font-serif", balance >= 0 ? "income-text" : "expense-text")}>
                        {formatCurrency(balance)}
                      </p>
                    </div>
                    <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", balance >= 0 ? "income-badge" : "expense-badge")}>
                      <Wallet className="w-4.5 h-4.5" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t.dashboard.income}</p>
                      <p className="text-xl font-bold mt-1 font-serif income-text">{formatCurrency(income)}</p>
                    </div>
                    <div className="w-9 h-9 rounded-xl income-badge flex items-center justify-center">
                      <ArrowUpRight className="w-4.5 h-4.5" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t.dashboard.expenses}</p>
                      <p className="text-xl font-bold mt-1 font-serif expense-text">{formatCurrency(expense)}</p>
                    </div>
                    <div className="w-9 h-9 rounded-xl expense-badge flex items-center justify-center">
                      <ArrowDownRight className="w-4.5 h-4.5" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t.dashboard.savingsRate}</p>
                      <p className={cn("text-xl font-bold mt-1 font-serif", income > 0 && balance >= 0 ? "income-text" : "expense-text")}>
                        {income > 0 ? `${Math.round((balance / income) * 100)}%` : "—"}
                      </p>
                    </div>
                    <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", balance >= 0 ? "income-badge" : "expense-badge")}>
                      {balance >= 0 ? <TrendingUp className="w-4.5 h-4.5" /> : <TrendingDown className="w-4.5 h-4.5" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Bar Chart */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{t.dashboard.incomeVsExpenses}</CardTitle>
            </CardHeader>
            <CardContent>
              {totalsLoading ? (
                <Skeleton className="h-48" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyTotals} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="income" name={t.dashboard.income} fill="var(--income)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expense" name={t.dashboard.expenses} fill="var(--expense)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{t.dashboard.expensesByCategory}</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                  {t.dashboard.noExpenses}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((_, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Line Chart */}
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{t.dashboard.balanceEvolution}</CardTitle>
          </CardHeader>
          <CardContent>
            {totalsLoading ? (
              <Skeleton className="h-40" />
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={lineData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    name={t.reports.balance}
                    stroke="var(--primary)"
                    strokeWidth={2}
                    dot={{ fill: "var(--primary)", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="border-border shadow-sm">
            <CardHeader className="pb-3 space-y-3">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-semibold">{t.dashboard.recentTransactions}</CardTitle>
                <Button variant="ghost" size="sm" className="text-primary text-xs" onClick={() => navigate("/transactions")}>
                  {ui.common.viewAll}
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={transactionSearch}
                    onChange={(event) => setTransactionSearch(event.target.value)}
                    placeholder={t.transactions.search}
                    aria-label={t.transactions.search}
                    className="pl-9"
                  />
                </div>
                <Select value={transactionType} onValueChange={setTransactionType}>
                  <SelectTrigger className="sm:w-32" aria-label={t.transactions.type}><SelectValue placeholder={t.transactions.type} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.transactions.all}</SelectItem>
                    <SelectItem value="income">{t.transactions.income}</SelectItem>
                    <SelectItem value="expense">{t.transactions.expense}</SelectItem>
                    <SelectItem value="transfer">{t.transactions.transfer}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={transactionCategory} onValueChange={setTransactionCategory}>
                  <SelectTrigger className="sm:w-48" aria-label={t.transactions.category}><SelectValue placeholder={t.transactions.category} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.transactions.all}</SelectItem>
                    {categories.map((category) => <SelectItem key={category.id} value={String(category.id)}>{categoryLabel(category.id)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
          <CardContent className="p-0">
            {txLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : filteredRecentTx.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                <p className="text-3xl mb-2">💸</p>
                <p>{t.dashboard.noTransactions}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowForm(true)}>
                  {t.transactions.addTransaction}
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredRecentTx.map((tx) => {
                  const cat = categories.find((c) => c.id === tx.categoryId);
                  return (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                      <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center text-sm flex-shrink-0",
                        tx.type === "income" ? "income-badge" : tx.type === "transfer" ? "bg-blue-100 text-blue-600" : "expense-badge")}>
                        {tx.type === "income" ? "↑" : tx.type === "transfer" ? "↔" : "↓"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.description || categoryName(cat?.name) || ui.common.noDescription}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(tx.date, { day: "2-digit", month: "2-digit", year: "numeric" })}
                          {cat && <> · {categoryLabel(cat.id)}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {tx.aiCategorized && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5">IA</Badge>
                        )}
                        <span className={cn("text-sm font-semibold tabular-nums",
                          tx.type === "income" ? "income-text" : tx.type === "transfer" ? "text-blue-600" : "expense-text")}>
                          {tx.type === "income" ? "+" : tx.type === "transfer" ? "↔" : "-"}{formatCurrency(parseFloat(String(tx.amount)))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bills due soon widget */}
      {billsDueSoon.length > 0 && (
        <div className="px-4 pb-4">
          <Card className="border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/30">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <CardTitle className="text-sm font-semibold text-orange-700 dark:text-orange-300">{t.bills.dueSoon}</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate("/bills")}>
                {ui.common.viewAll}
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {billsDueSoon.slice(0, 3).map((bill) => {
                  const due = new Date(bill.dueDate);
                  const today = new Date();
                  const isOverdue = due < today;
                  const isToday = due.toDateString() === today.toDateString();
                  return (
                    <div key={bill.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${bill.type === 'payable' ? 'bg-red-500' : 'bg-green-500'}`} />
                        <span className="font-medium">{bill.title}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : isToday ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' : 'bg-muted text-muted-foreground'}`}>
                          {isOverdue ? t.bills.overdue : isToday ? t.bills.dueToday : formatDate(due, { day: "2-digit", month: "2-digit", year: "numeric" })}
                        </span>
                      </div>
                      <span className={`font-semibold ${bill.type === 'payable' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {formatCurrency(parseFloat(String(bill.amount)))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <TransactionForm open={showForm} onClose={() => setShowForm(false)} />
    </BolsoLayout>
  );
}
